import React from 'react';
import { AppShell } from '../../components/layout/AppShell';
import { PageLayout } from '../../components/common/PageLayout';
import { ContentCard } from '../../components/common/ContentCard';
import { Button } from '../../components/common/Button';
import { useTestPdfGenerationMutation } from '../../store/slices/apiSlice';
import { FileText } from 'lucide-react';

export const TestPdfPage: React.FC = () => {
  const [testPdf, { isLoading }] = useTestPdfGenerationMutation();

  const handleTestPdf = async () => {
    try {
      await testPdf().unwrap();
      alert('PDF downloaded successfully! Check your downloads folder.');
    } catch (error: any) {
      console.error('PDF generation failed:', error);
      alert('Failed to generate PDF. Check console for details.');
    }
  };

  return (
    <AppShell>
      <PageLayout
        title="PDF Test Page"
        subtitle="Test PDF generation functionality"
        bgColor="bg-blue-50"
        textColor="text-blue-900"
        borderColor="border-blue-200"
        icon={<FileText className="w-6 h-6" />}
      >
        <ContentCard>
          <div className="space-y-4">
            <h2 className="text-xl font-semibold">PDF Generation Test</h2>
            <p className="text-gray-600">
              Click the button below to generate a test PDF using ReportLab.
              This will verify that the PDF generation system is working correctly.
            </p>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-medium mb-2">What this test does:</h3>
              <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                <li>Generates a test PDF using ReportLab</li>
                <li>Creates a simple document with test content</li>
                <li>Downloads the PDF to your browser</li>
                <li>Confirms the PDF generation system is functional</li>
              </ul>
            </div>

            <Button
              onClick={handleTestPdf}
              disabled={isLoading}
              variant="primary"
              icon={<FileText className="w-4 h-4" />}
            >
              {isLoading ? 'Generating PDF...' : 'Generate Test PDF'}
            </Button>

            <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                <strong>Note:</strong> If the PDF doesn&apos;t download, check:
                <ul className="list-disc list-inside mt-2">
                  <li>Browser console for errors</li>
                  <li>Network tab in DevTools</li>
                  <li>Backend logs for ReportLab errors</li>
                </ul>
              </p>
            </div>
          </div>
        </ContentCard>
      </PageLayout>
    </AppShell>
  );
};
